import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Input,
  Box,
  Flex,
  Radio,
  RadioGroup,
  Spinner,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import {
  ArrowClockwise,
  ClockCounterClockwise,
  Eraser,
  PaperPlaneTilt,
  Plus,
  Trash,
  FileText,
  Warning,
} from 'phosphor-react';
import { toast } from 'sonner';
import { useKanban, IKanbanAnexo } from '../../hooks/useKanban';
import {
  useAutentique,
  IAutentiqueDocumentoStatus,
  IAutentiqueSignatarioStatus,
  IAutentiqueSigner,
  IAssinanteSugerido,
} from '../../hooks/useAutentique';

interface AutentiqueSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: number;
  cardTitle: string;
  hasAnexos: boolean;
  onSuccess: () => void;
  /** Preenche os signatários com quem assina o contrato do card (eventos); o operador confere. */
  sugerirAssinantes?: boolean;
}

const ROTULO_PAPEL: Record<NonNullable<IAssinanteSugerido['papel']>, string> = {
  contratante: 'Contratante',
  contratada: 'Contratada',
  testemunha: 'Testemunha',
};

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const problemaDa = (s: IAutentiqueSigner) => {
  if (!s.nome.trim() && !s.email.trim()) return 'Linha vazia';
  if (!s.nome.trim()) return 'Falta o nome';
  if (!EMAIL_VALIDO.test(s.email.trim())) return 'E-mail inválido';
  return null;
};

const assinantes = (n: number) => `${n} assinante${n === 1 ? '' : 's'}`;
const qtdLinhas = (n: number) => `${n} ${n === 1 ? 'linha' : 'linhas'}`;

export const AutentiqueSignModal: React.FC<AutentiqueSignModalProps> = ({
  isOpen,
  onClose,
  cardId,
  cardTitle,
  hasAnexos,
  onSuccess,
  sugerirAssinantes = false,
}) => {
  const { getAnexos } = useKanban();
  const { enviarParaAssinatura, getAssinantesSugeridos, getStatus } = useAutentique();
  const sigIdCounter = useRef(1);
  // Sugestão que chega depois de fechar/reabrir é descartada; a digitada nunca é sobrescrita.
  const abertura = useRef(0);
  // A consulta mais nova vale: trocar de anexo no meio descarta a resposta antiga.
  const pedido = useRef(0);
  const editouSignatarios = useRef(false);
  // Reler confirmado vale por cima da lista editada; editar de novo durante a leitura desfaz isso.
  const substituirAoLer = useRef(false);
  // Com muitos assinantes a linha nova nasce fora da vista: o foco a traz.
  const focarUid = useRef<number | null>(null);
  const camposNome = useRef<Record<number, HTMLInputElement | null>>({});

  type SignatarioWithId = IAutentiqueSigner & {
    uid: number;
    papel?: IAssinanteSugerido['papel'];
    recusou?: boolean;
  };

  const [anexos, setAnexos] = useState<IKanbanAnexo[]>([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [selectedAnexoId, setSelectedAnexoId] = useState<string>('');
  const [nomeDocumento, setNomeDocumento] = useState('');
  const [signatarios, setSignatarios] = useState<SignatarioWithId[]>([
    { uid: 0, nome: '', email: '' },
  ]);
  const [leitura, setLeitura] = useState<{ texto: string; cor: string } | null>(null);
  const [lendo, setLendo] = useState(false);
  const [anterior, setAnterior] = useState<IAutentiqueDocumentoStatus | null>(null);
  const [confirmacao, setConfirmacao] = useState<{ texto: string; acao: () => void } | null>(null);
  // A linha em digitação não reclama: "E-mail inválido" na primeira letra só atrapalha.
  const [digitando, setDigitando] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  const preencher = (linhas: SignatarioWithId[]) => {
    sigIdCounter.current = linhas.length;
    editouSignatarios.current = false;
    setSignatarios(linhas);
  };

  // `substituir`: Reler confirmado. A proteção da lista só sai se a leitura trouxer assinantes.
  const ler = (anexoId?: number, substituir = false) => {
    substituirAoLer.current = substituir;
    pedido.current += 1;
    const estePedido = pedido.current;
    const avisoDaLista = signatarios.some(s => s.email.trim())
      ? 'A lista abaixo não mudou: confira.'
      : '';
    setLendo(true);
    setLeitura(null);
    getAssinantesSugeridos(cardId, anexoId)
      .then(({ assinantes: lidos, documento }) => {
        if (estePedido !== pedido.current) return;
        const resumo = `Lido de ${documento ?? 'documento'}: ${assinantes(lidos.length)}`;
        if (!lidos.length) {
          setLeitura({
            texto: `Não encontrei assinantes neste documento. ${avisoDaLista || 'Preencha à mão.'}`,
            cor: 'orange.600',
          });
        } else if (editouSignatarios.current && !substituirAoLer.current) {
          setLeitura({
            texto: `${resumo}. A lista já foi editada: use Reler para substituir.`,
            cor: 'orange.600',
          });
        } else {
          preencher(
            lidos.map((s, i) => ({ uid: i, nome: s.nome, email: s.email, papel: s.papel })),
          );
          setLeitura({ texto: `${resumo}. Confira antes de enviar.`, cor: 'gray.500' });
        }
      })
      .catch(() => {
        if (estePedido !== pedido.current) return;
        setLeitura({
          texto: `Não consegui ler o documento. ${
            avisoDaLista || 'Tente Reler ou preencha à mão.'
          }`,
          cor: 'red.500',
        });
      })
      .finally(() => {
        if (estePedido === pedido.current) setLendo(false);
      });
  };

  useEffect(() => {
    abertura.current += 1;
    pedido.current += 1;
    const estaAbertura = abertura.current;
    if (isOpen && hasAnexos && cardId) {
      setLoadingAnexos(true);
      getAnexos(cardId)
        .then(res => {
          const data = res?.data ?? [];
          setAnexos(data);
          if (data.length === 1) {
            setSelectedAnexoId(String(data[0].id));
            setNomeDocumento(data[0].nome);
          }
        })
        .catch(() => toast.error('Erro ao carregar anexos'))
        .finally(() => setLoadingAnexos(false));
      if (sugerirAssinantes) {
        ler();
        getStatus(cardId)
          .then(res => {
            if (estaAbertura !== abertura.current) return;
            setAnterior((res?.data ?? []).find(doc => doc.signatarios?.length) ?? null);
          })
          .catch(() => {}); // sem o histórico, só não aparece o botão
      }
    }
    if (!isOpen) {
      setAnexos([]);
      setSelectedAnexoId('');
      setNomeDocumento('');
      sigIdCounter.current = 1;
      editouSignatarios.current = false;
      setSignatarios([{ uid: 0, nome: '', email: '' }]);
      setLeitura(null);
      setLendo(false);
      setAnterior(null);
      setConfirmacao(null);
      setDigitando(null);
      setSending(false);
    }
  }, [isOpen, cardId, hasAnexos]);

  const handleAnexoSelect = (val: string) => {
    setSelectedAnexoId(val);
    setConfirmacao(null);
    const a = anexos.find(x => String(x.id) === val);
    if (a) setNomeDocumento(a.nome);
    if (!sugerirAssinantes || val === selectedAnexoId) return;
    // A lista tem de ser a do documento que vai assinar. Editada, fica, e a tela diz que ficou.
    if (editouSignatarios.current) {
      pedido.current += 1;
      setLendo(false);
      setLeitura({
        texto: 'A lista foi mantida: use Reler para ler este documento.',
        cor: 'orange.600',
      });
    } else {
      ler(Number(val));
    }
  };

  const marcarEdicao = () => {
    editouSignatarios.current = true;
    substituirAoLer.current = false;
  };

  const addSignatario = () => {
    marcarEdicao();
    const newId = sigIdCounter.current;
    sigIdCounter.current += 1;
    focarUid.current = newId;
    setDigitando(newId);
    setSignatarios(prev => [...prev, { uid: newId, nome: '', email: '' }]);
  };

  const removeSignatario = (idx: number) => {
    marcarEdicao();
    setSignatarios(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSignatario = (idx: number, field: 'nome' | 'email', value: string) => {
    marcarEdicao();
    setDigitando(signatarios[idx].uid);
    setSignatarios(prev =>
      prev.map((s, i) =>
        i === idx ? { ...s, [field]: value, recusou: field !== 'email' && s.recusou } : s,
      ),
    );
  };

  const limpar = () => {
    pedido.current += 1;
    setLendo(false);
    setLeitura(null);
    focarUid.current = 0;
    preencher([{ uid: 0, nome: '', email: '' }]);
  };

  const usarAnterior = () => {
    if (!anterior) return;
    // Só o banco, sem consultar a Autentique: a recusa encerra o documento e fica `cancelled` em quem recusou.
    const recusou = (s: IAutentiqueSignatarioStatus) =>
      anterior.status === 'cancelled' && s.status === 'cancelled';
    preencher(
      anterior.signatarios.map((s, i) => ({
        uid: i,
        nome: s.nome,
        email: s.email,
        recusou: recusou(s),
      })),
    );
    const recusas = anterior.signatarios.filter(recusou).length;
    const assinaram = anterior.signatarios.filter(s => s.status === 'signed').length;
    setLeitura({
      texto: [
        `Do envio anterior: ${assinantes(anterior.signatarios.length)}.`,
        recusas ? 'Quem recusou está marcado: confira o e-mail.' : '',
        assinaram ? `Quem já assinou (${assinaram}) assina de novo.` : '',
      ]
        .filter(Boolean)
        .join(' '),
      cor: recusas ? 'orange.600' : 'gray.500',
    });
  };

  const temConteudo = signatarios.some(s => s.nome.trim() || s.email.trim());
  const editado = editouSignatarios.current && temConteudo;
  // Substituir a lista pergunta antes de apagar o que o operador editou.
  const substituirLista = (texto: string, acao: () => void) => {
    if (editado) {
      setConfirmacao({ texto, acao });
    } else {
      setConfirmacao(null);
      acao();
    }
  };
  // Todo jeito de fechar passa aqui: fechar zera a lista.
  const fechar = () => {
    if (editado) setConfirmacao({ texto: 'Fechar e perder o que foi editado?', acao: onClose });
    else onClose();
  };

  const problemas = signatarios.map(problemaDa);
  const comProblema = problemas.filter(Boolean).length;
  const contagem = signatarios.reduce((mapa, s) => {
    const email = s.email.trim().toLowerCase();
    return email ? mapa.set(email, (mapa.get(email) ?? 0) + 1) : mapa;
  }, new Map<string, number>());
  const repetido = (s: SignatarioWithId) => (contagem.get(s.email.trim().toLowerCase()) ?? 0) > 1;
  // Problema trava o envio; recusa e e-mail repetido só pedem um olhar. A linha em digitação espera.
  const aCorrigir = problemas.map((p, i) => !!p && signatarios[i].uid !== digitando);
  const aConferir = signatarios.map((s, i) => !problemas[i] && (!!s.recusou || repetido(s)));
  const nCorrigir = aCorrigir.filter(Boolean).length;
  const nConferir = aConferir.filter(Boolean).length;
  // Com 78 linhas a que importa fica fora da tela: o rodapé leva até ela.
  const irParaAtencao = () => {
    const idx = nCorrigir ? aCorrigir.indexOf(true) : aConferir.indexOf(true);
    camposNome.current[signatarios[idx].uid]?.focus();
  };

  const isValid =
    selectedAnexoId &&
    nomeDocumento.trim() &&
    signatarios.length > 0 &&
    comProblema === 0 &&
    !lendo;

  const handleEnviar = async () => {
    if (sending) return;
    if (!isValid) return;
    setSending(true);
    try {
      await enviarParaAssinatura({
        card_id: cardId,
        anexo_id: Number(selectedAnexoId),
        nome_documento: nomeDocumento,
        signatarios: signatarios.map(({ nome, email }) => ({ nome, email })),
      });
      toast.success('Documento enviado para assinatura!');
      onSuccess();
    } catch (err: any) {
      // Erro de validação do back vem como lista: objeto no toast derruba a tela, e a lista se perde.
      const detail = err?.response?.data?.detail;
      const invalida = Array.isArray(detail)
        ? detail.find((d: any) => d?.loc?.[1] === 'signatarios')
        : null;
      const msg =
        typeof detail === 'string'
          ? detail
          : (invalida &&
              `${invalida.loc[3] === 'nome' ? 'Nome' : 'E-mail'} não aceito na linha ${
                invalida.loc[2] + 1
              }. Corrija e envie de novo.`) ||
            err?.message ||
            'Erro ao enviar para assinatura';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Sem anexos
  if (!hasAnexos) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="md">Documento necessário</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex direction="column" align="center" py={4} gap={3}>
              <Icon as={Warning} boxSize={10} color="orange.400" />
              <Text fontSize="sm" color="gray.600" textAlign="center">
                O card <strong>{cardTitle}</strong> não tem documentos anexados.
                <br />
                Adicione um documento antes de enviar para assinatura.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={onClose}>
              Entendi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    // Rolagem por dentro: com muitos assinantes (lista de presença, 30+) o botão de envio sumia da tela.
    <Modal isOpen={isOpen} onClose={fechar} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md">Enviar para assinatura</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Selecionar anexo */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="600"
                color="gray.500"
                mb={2}
                textTransform="uppercase"
              >
                Selecionar documento
              </Text>
              {loadingAnexos && (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                </Flex>
              )}
              {!loadingAnexos && anexos.length === 0 && (
                <Flex direction="column" align="center" py={4} gap={3}>
                  <Icon as={Warning} boxSize={8} color="orange.400" />
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Nenhum anexo encontrado. Adicione um arquivo ao card primeiro.
                  </Text>
                </Flex>
              )}
              {!loadingAnexos && anexos.length > 0 && (
                <RadioGroup value={selectedAnexoId} onChange={handleAnexoSelect}>
                  <VStack spacing={2} align="stretch">
                    {anexos.map(a => (
                      <Box
                        key={a.id}
                        p={2.5}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={selectedAnexoId === String(a.id) ? 'blue.300' : 'gray.100'}
                        bg={selectedAnexoId === String(a.id) ? 'blue.50' : 'white'}
                        cursor="pointer"
                        onClick={() => handleAnexoSelect(String(a.id))}
                        transition="all 0.15s"
                      >
                        <HStack spacing={2}>
                          <Radio value={String(a.id)} size="sm" colorScheme="blue" />
                          <Icon as={FileText} color="gray.400" boxSize={4} />
                          <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                            {a.nome}
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </RadioGroup>
              )}
            </Box>

            {/* Nome do documento */}
            {selectedAnexoId && (
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  color="gray.500"
                  mb={1.5}
                  textTransform="uppercase"
                >
                  Nome do documento
                </Text>
                <Input
                  size="sm"
                  value={nomeDocumento}
                  onChange={e => setNomeDocumento(e.target.value)}
                  placeholder="Nome que aparecerá na Autentique"
                  borderRadius="md"
                />
              </Box>
            )}

            {/* Signatários */}
            {selectedAnexoId && (
              <Box>
                {/* Fixo no topo enquanto a lista rola: o "Adicionar" segue a mão com 30+ assinantes. */}
                <Box mb={2} position="sticky" top={-2} pt={2} zIndex={1} bg="white">
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1}>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Signatários
                      </Text>
                      {temConteudo && (
                        <Text fontSize="xs" color="gray.400">
                          ({problemas.filter(p => p !== 'Linha vazia').length})
                        </Text>
                      )}
                    </HStack>
                    <HStack spacing={0}>
                      {sugerirAssinantes && (
                        <Button
                          size="xs"
                          variant="ghost"
                          leftIcon={<ArrowClockwise size={12} />}
                          isDisabled={lendo}
                          onClick={() =>
                            substituirLista(
                              'Substituir a lista pelo que está no documento?',
                              () => {
                                ler(Number(selectedAnexoId), true);
                              },
                            )
                          }
                        >
                          Reler
                        </Button>
                      )}
                      {sugerirAssinantes && anterior && (
                        <Button
                          size="xs"
                          variant="ghost"
                          leftIcon={<ClockCounterClockwise size={12} />}
                          isDisabled={lendo}
                          onClick={() =>
                            substituirLista(
                              'Substituir a lista pela do envio anterior?',
                              usarAnterior,
                            )
                          }
                        >
                          Envio anterior
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={<Eraser size={12} />}
                        isDisabled={!temConteudo}
                        onClick={() =>
                          setConfirmacao({ texto: 'Apagar toda a lista?', acao: limpar })
                        }
                      >
                        Limpar
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Plus size={12} />}
                        onClick={addSignatario}
                      >
                        Adicionar
                      </Button>
                    </HStack>
                  </Flex>
                </Box>
                {/* Lista grande leva segundos: em letra miúda, a linha vazia parecia tela parada. */}
                {lendo && (
                  <HStack spacing={3} mb={3} p={3} bg="blue.50" borderRadius="md" role="status">
                    <Spinner size="sm" color="blue.500" />
                    <Box>
                      <Text fontSize="sm" fontWeight="500" color="gray.700">
                        Lendo os assinantes do documento...
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Listas grandes levam alguns segundos.
                      </Text>
                    </Box>
                  </HStack>
                )}
                {leitura && (
                  <Text fontSize="xs" color={leitura.cor} mb={2}>
                    {leitura.texto}
                  </Text>
                )}
                <VStack spacing={2} align="stretch">
                  {signatarios.map((s, idx) => (
                    <Box key={`sig-${s.uid}`}>
                      {s.papel && (
                        <Text fontSize="xs" color="gray.500" mb={1}>
                          {ROTULO_PAPEL[s.papel]}
                        </Text>
                      )}
                      <HStack
                        spacing={2}
                        onBlur={e => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                            setDigitando(null);
                          }
                        }}
                      >
                        <Input
                          ref={el => {
                            camposNome.current[s.uid] = el;
                            if (el && focarUid.current === s.uid) {
                              focarUid.current = null;
                              el.focus();
                            }
                          }}
                          size="sm"
                          placeholder="Nome"
                          value={s.nome}
                          onChange={e => updateSignatario(idx, 'nome', e.target.value)}
                          isInvalid={digitando !== s.uid && problemas[idx] === 'Falta o nome'}
                          scrollMarginTop="48px"
                          borderRadius="md"
                          flex={1}
                        />
                        <Input
                          size="sm"
                          placeholder="Email"
                          type="email"
                          value={s.email}
                          onChange={e => updateSignatario(idx, 'email', e.target.value)}
                          isInvalid={digitando !== s.uid && problemas[idx] === 'E-mail inválido'}
                          borderRadius="md"
                          flex={1}
                        />
                        {signatarios.length > 1 && (
                          <IconButton
                            aria-label="Remover"
                            icon={<Trash size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeSignatario(idx)}
                          />
                        )}
                      </HStack>
                      {digitando !== s.uid &&
                        problemas[idx] &&
                        (problemas[idx] !== 'Linha vazia' || signatarios.length > 1) && (
                          <Text fontSize="xs" color="red.500" mt={1}>
                            {problemas[idx]}
                          </Text>
                        )}
                      {!problemas[idx] && repetido(s) && (
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          E-mail repetido na lista
                        </Text>
                      )}
                      {s.recusou && (
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Recusou o envio anterior: confira o e-mail.
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        {/* Fora da área que rola: com a lista abaixo da dobra, a pergunta ficava invisível. */}
        {confirmacao && (
          <Flex
            justify="space-between"
            align="center"
            gap={2}
            mx={6}
            mb={2}
            p={2}
            bg="orange.50"
            borderRadius="md"
          >
            <Text fontSize="xs">{confirmacao.texto}</Text>
            <HStack spacing={1}>
              <Button size="xs" variant="ghost" onClick={() => setConfirmacao(null)}>
                Manter
              </Button>
              <Button
                size="xs"
                colorScheme="orange"
                onClick={() => {
                  confirmacao.acao();
                  setConfirmacao(null);
                }}
              >
                Confirmar
              </Button>
            </HStack>
          </Flex>
        )}
        <ModalFooter gap={2}>
          {selectedAnexoId && temConteudo && nCorrigir + nConferir > 0 && (
            <Button
              size="xs"
              variant="link"
              colorScheme={nCorrigir ? 'red' : 'orange'}
              mr="auto"
              leftIcon={<Warning size={14} />}
              onClick={irParaAtencao}
            >
              {nCorrigir
                ? `${qtdLinhas(nCorrigir)} com problema`
                : `${qtdLinhas(nConferir)} para conferir`}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={fechar} isDisabled={sending}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<PaperPlaneTilt size={14} />}
            onClick={handleEnviar}
            isLoading={sending || lendo}
            loadingText={sending ? 'Enviando...' : 'Lendo assinantes...'}
            isDisabled={!isValid}
          >
            Enviar para assinatura
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
